// BusinessRulesBuilder.jsx
// Created by Claude on 2025-11-16
// Purpose: Business Rules Builder integrated with existing validation service
// Replaces ValidationConfigForm with no dummy data

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import validationService from '../../services/validationService';
import dataManagementService from '../../services/dataManagementService';
import { createLogger } from '../../utils/logger';
import { ProfileIcon, ProfileMenu, ProfileMenuItem } from '../Header/ProfileMenu';
import { FaUserCircle } from 'react-icons/fa';
import { IoSettingsOutline } from 'react-icons/io5';
import { FiLogOut } from 'react-icons/fi';
import Logout from '../Logout/Logut';
import useProfileStore from '../../store/useProfileStore';
import logo from '../../assets/images/newLogo.png';

const logger = createLogger("src/components/Validations/BusinessRulesBuilder.jsx");

// Resolve API base URL
const API_BASE_URL = 
  import.meta.env?.VITE_API_BASE || 
  process.env?.REACT_APP_API_BASE || 
  window.__API_BASE__ || 
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const OPERATORS = {
  string: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'IN'],
  number: ['EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'LESS_THAN', 'GREATER_OR_EQUAL', 'LESS_OR_EQUAL', 'BETWEEN'],
  boolean: ['IS_TRUE', 'IS_FALSE']
};

const RULE_CATEGORIES = [
  { id: 'STATIC', name: 'Static Rules', icon: 'âš¡', bgColor: '#FEF3C7', textColor: '#92400E' },
  { id: 'AUTHORIZATION', name: 'Authorization', icon: 'ðŸ”', bgColor: '#DBEAFE', textColor: '#1E40AF' },
  { id: 'VALIDATION', name: 'Validation', icon: 'âœ…', bgColor: '#D1FAE5', textColor: '#065F46' },
  { id: 'BUSINESS_RULE', name: 'Business Rule', icon: 'ðŸ’¼', bgColor: '#E9D5FF', textColor: '#6B21A8' }
];

// Validation type definitions with required fields (from default-validation-builder)
const VALIDATION_TYPES = {
  ALWAYS_SHOW: { label: 'Always Show', fields: [] },
  NEVER_SHOW: { label: 'Never Show', fields: [] },
  DATE_RANGE: { label: 'Date Range', fields: ['startDate', 'endDate'] },
  TIME_RANGE: { label: 'Time Range', fields: ['startTime', 'endTime'] },
  WEEKDAY_ONLY: { label: 'Weekday Only (Mon-Fri)', fields: [] },
  WEEKEND_ONLY: { label: 'Weekend Only (Sat-Sun)', fields: [] },
  MOBILE_DEVICE: { label: 'Mobile Device', fields: [] },
  DESKTOP_DEVICE: { label: 'Desktop Device', fields: [] },
  SPECIFIC_DAY: { label: 'Specific Day', fields: ['day'] },
  BEFORE_DATE: { label: 'Before Date', fields: ['cutoffDate'] },
  AFTER_DATE: { label: 'After Date', fields: ['startDate'] },
  BUSINESS_HOURS: { label: 'Business Hours (Mon-Fri 9AM-5PM)', fields: [] },
  SPECIFIC_MONTH: { label: 'Specific Month', fields: ['month'] },
  SEASON: { label: 'Season', fields: ['season'] },
  BROWSER_TYPE: { label: 'Browser Type', fields: ['browser'] },
  SCREEN_WIDTH: { label: 'Screen Width', fields: ['minWidth', 'maxWidth'] },
  FEATURE_FLAG: { label: 'Feature Flag', fields: ['flagName', 'enabled'] }
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SEASONS = ['Spring', 'Summer', 'Fall', 'Winter'];
const BROWSERS = ['Chrome', 'Firefox', 'Safari', 'Edge', 'Opera'];

const BusinessRulesBuilder = ({ appName, dbName = "jayram" }) => {
  logger.entry("BusinessRulesBuilder", "Component mounted", { appName });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userImage = useProfileStore((state) => state.userImage);

  const [view, setView] = useState('library'); // 'library', 'builder', 'template-builder'
  const [rules, setRules] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loadingRules, setLoadingRules] = useState(true);
  const [loadingCollections, setLoadingCollections] = useState(true);

  const [currentRule, setCurrentRule] = useState({
    ruleId: '',
    ruleName: '',
    ruleCategory: 'AUTHORIZATION',
    description: '',
    conditions: [{ id: 1, logicalOperator: 'IF', collection: '', field: '', operator: '', value: '' }],
    status: 'ACTIVE',
    // Message fields
    errorMessage: '',
    successMessage: '',
    warningMessage: '',
    // Fallback fields
    fallbackText: '',
    fallbackComponent: '',
    hideSpace: false
  });

  const [editingRule, setEditingRule] = useState(null);

  // Template Builder States (from default-validation-builder)
  const [validationType, setValidationType] = useState('ALWAYS_SHOW');
  const [validationName, setValidationName] = useState('');
  const [validationConfig, setValidationConfig] = useState({});
  const [savedValidations, setSavedValidations] = useState([]);
  const [selectedValidationId, setSelectedValidationId] = useState('');
  const [previewResult, setPreviewResult] = useState(null);
  const [showTemplateAccordion, setShowTemplateAccordion] = useState(false);
  const [showFormValidationsAccordion, setShowFormValidationsAccordion] = useState(false);
  const [hasForms, setHasForms] = useState(false);
  
  // Message fields for template validations
  const [templateMessages, setTemplateMessages] = useState({
    errorMessage: '',
    successMessage: '',
    warningMessage: ''
  });
  
  // Test data for business rules
  const [testData, setTestData] = useState({});
  const [businessRuleTestResult, setBusinessRuleTestResult] = useState(null);

  // Load rules from API using existing validation service
  useEffect(() => {
    loadRulesFromAPI();
  }, [appName]);

  // Load available collections dynamically
  useEffect(() => {
    // alert(`ðŸš€ DEBUG: Component mounted/appName changed\nappName: ${appName}\nCalling loadAvailableCollections...`);
    loadAvailableCollections();
  }, [appName]);

  // Check if app has forms
  useEffect(() => {
    checkForForms();
  }, [appName]);

  const loadRulesFromAPI = async () => {
    if (!appName) {
      logger.warn("loadRulesFromAPI", "No appName provided");
      setLoadingRules(false);
      return;
    }

    try {
      setLoadingRules(true);
      
      // Normalize appName to lowercase for backend consistency
      const normalizedAppName = appName.toLowerCase();
      
      logger.info("loadRulesFromAPI", "Fetching rules via validationService", { appName, normalizedAppName });
      
      const data = await validationService.getAllValidations(normalizedAppName, dbName);
      
      // Filter out static templates (identified by validationType field) - they appear in separate accordion
      const businessRules = data.filter(v => !v.validationType);
      
      // Convert to expected format
      const convertedRules = businessRules.map(v => ({
        ruleId: v.id || v.ruleId || `RULE_${Date.now()}`,
        ruleName: v.name || v.ruleName || 'Untitled Rule',
        ruleCategory: v.ruleCategory || (v.type === 'visibility' ? 'VALIDATION' : 'BUSINESS_RULE'),
        description: v.description || '',
        conditions: v.conditions || [{ id: 1, logicalOperator: 'IF', collection: '', field: '', operator: '', value: '' }],
        status: v.status || 'ACTIVE',
        usageCount: v.usageCount || 0,
        usedIn: v.usedIn || [],
        errorMessage: v.errorMessage || '',
        successMessage: v.successMessage || '',
        warningMessage: v.warningMessage || '',
        fallbackText: v.fallbackText || '',
        fallbackComponent: v.fallbackComponent || '',
        hideSpace: v.hideSpace || false
      }));

      setRules(convertedRules);
      logger.info("loadRulesFromAPI", "Rules loaded successfully", { count: convertedRules.length });
    } catch (error) {
      logger.error("loadRulesFromAPI", "Failed to load rules", error);
      setRules([]);
    } finally {
      setLoadingRules(false);
    }
  };

  const loadAvailableCollections = async () => {
    if (!appName) {
      // alert('âš ï¸ DEBUG: No appName provided!');
      setLoadingCollections(false);
      return;
    }

    try {
      setLoadingCollections(true);
      
      // Convert appName to lowercase for backend consistency
      const normalizedAppName = appName.toLowerCase();
      
      // alert(`ðŸ” DEBUG: Starting to load tables\n\nOriginal App Name: ${appName}\nNormalized (lowercase): ${normalizedAppName}\nDB Name: ${dbName}\nAPI Base: ${API_BASE_URL}`);
      logger.info("loadAvailableCollections", "Fetching table schemas from Data Management", { appName, normalizedAppName });

      // Use Data Management service to get table schemas with normalized name
      const schemas = await dataManagementService.getTableSchemas(normalizedAppName);
      
      // alert(`ðŸ“Š DEBUG: Fetched ${schemas.length} schemas from API\n\nSchemas: ${JSON.stringify(schemas.map(s => s.tableName), null, 2)}`);
      
      logger.info("loadAvailableCollections", "Schemas fetched", { 
        count: schemas.length,
        schemas: schemas.map(s => ({ displayName: s.tableName, internalName: s.internalName }))
      });

      // For each schema, fetch actual fields from the internal collection
      // alert(`ðŸ”„ DEBUG: Now fetching fields for ${schemas.length} tables...`);
      
      const mappedCollections = await Promise.all(
        schemas.map(async (schema, index) => {
          try {
            // Fetch one record from the internal collection to get actual fields
            const internalCollectionName = schema.internalName || `${normalizedAppName}_data_${schema.id}`;
            const url = `${API_BASE_URL}/api/collection/${encodeURIComponent(dbName)}/${encodeURIComponent(normalizedAppName)}/${encodeURIComponent(internalCollectionName)}`;
            
            // alert(`ðŸ“ DEBUG [${index + 1}/${schemas.length}]: Fetching fields for "${schema.tableName}"\nInternal: ${internalCollectionName}\nURL: ${url}`);
            
            logger.debug("loadAvailableCollections", `Fetching fields from: ${internalCollectionName}`, { url });
            const response = await fetch(url, { headers: { "Content-Type": "application/json" } });
            
            let fields = [];
            if (response.ok) {
              const data = await response.json();
              // alert(`âœ… DEBUG: Got ${data?.length || 0} records from ${internalCollectionName}`);
              
              if (data && data.length > 0) {
                const firstDoc = data[0];
                // Extract field names from actual document, excluding system fields
                fields = Object.keys(firstDoc)
                  .filter(key => !['id', '_id', 'createdBy', 'createdAt', 'updatedBy', 'updatedAt'].includes(key))
                  .map(key => ({
                    id: key,
                    name: formatFieldName(key),
                    type: inferFieldType(firstDoc[key])
                  }));
                
                // alert(`âœ… DEBUG: Extracted ${fields.length} fields: ${fields.map(f => f.id).join(', ')}`);
                logger.info("loadAvailableCollections", `âœ… Fetched ${fields.length} fields from ${internalCollectionName}`, { fields: fields.map(f => f.id) });
              }
            } else {
              // alert(`âš ï¸ DEBUG: Failed to fetch data (Status: ${response.status})`);
            }

            // Fallback to schema fields if no data exists
            if (fields.length === 0 && schema.fields && schema.fields.length > 0) {
              fields = schema.fields.map(field => ({
                id: field.name,
                name: field.name,
                type: mapFieldTypeToValidationType(field.type)
              }));
              // alert(`ðŸ”„ DEBUG: No data found, using ${fields.length} schema fields: ${fields.map(f => f.id).join(', ')}`);
              logger.warn("loadAvailableCollections", `No data in ${internalCollectionName}, using schema fields`, { fields: fields.map(f => f.id) });
            }

            return {
              id: schema.tableName,  // Use displayName as ID
              name: schema.tableName, // Display name for dropdown
              internalName: internalCollectionName,
              fields: fields
            };
          } catch (err) {
            // alert(`âŒ DEBUG: Error fetching fields for ${schema.tableName}: ${err.message}`);
            logger.error("loadAvailableCollections", `Failed to fetch fields for ${schema.tableName}`, err);
            // Return schema with empty fields if fetch fails
            return {
              id: schema.tableName,
              name: schema.tableName,
              internalName: schema.internalName,
              fields: []
            };
          }
        })
      );

      const finalCollections = mappedCollections.filter(c => c.fields.length > 0);
      setCollections(finalCollections);
      
      // alert(`ðŸŽ‰ DEBUG: FINAL RESULT\n\nTotal tables: ${finalCollections.length}\n\nTables with fields:\n${finalCollections.map(c => `- ${c.name} (${c.fields.length} fields)`).join('\n')}`);
      
      if (mappedCollections.length > 0) {
        console.log(`âœ… Found ${mappedCollections.length} tables:`, mappedCollections.map(c => c.name));
        logger.info("loadAvailableCollections", "Collections loaded successfully", { 
          collections: mappedCollections.map(c => ({ name: c.name, fieldCount: c.fields.length }))
        });
      } else {
        console.warn(`âš ï¸ No tables found for app: ${normalizedAppName}`);
        console.log(`ðŸ’¡ Please create data tables in Data Management first`);
      }
    } catch (error) {
      // alert(`âŒ DEBUG: CRITICAL ERROR!\n\nError: ${error.message}\n\nStack: ${error.stack}`);
      logger.error("loadAvailableCollections", "Failed to load tables", error);
      console.error("âŒ Table loading error:", error);
      setCollections([]);
    } finally {
      setLoadingCollections(false);
      // alert('âœ… DEBUG: Loading complete, collections state updated');
    }
  };

  // Helper function to map field types from Data Management to validation types
  const mapFieldTypeToValidationType = (fieldType) => {
    const typeMap = {
      'number': 'number',
      'currency': 'number',
      'percentage': 'number',
      'rating': 'number',
      'boolean': 'boolean',
      'text': 'string',
      'textarea': 'string',
      'email': 'string',
      'phone': 'string',
      'url': 'string',
      'date': 'string',
      'datetime': 'string',
      'select': 'string',
      'multiselect': 'string',
      'color': 'string',
      'file': 'string',
      'image': 'string',
      'json': 'string'
    };
    return typeMap[fieldType] || 'string';
  };

  const checkForForms = async () => {
    if (!appName) return;

    try {
      // Normalize appName to lowercase
      const normalizedAppName = appName.toLowerCase();
      
      logger.info("checkForForms", "Checking if app has forms", { appName, normalizedAppName });
      const url = `${API_BASE_URL}/api/collection/${encodeURIComponent(dbName)}/${encodeURIComponent(normalizedAppName)}/forms`;
      const response = await fetch(url, { headers: { "Content-Type": "application/json" } });
      
      if (response.ok) {
        const forms = await response.json();
        setHasForms(forms && forms.length > 0);
        logger.info("checkForForms", "Forms check complete", { hasForms: forms && forms.length > 0 });
      } else {
        setHasForms(false);
      }
    } catch (error) {
      logger.warn("checkForForms", "Failed to check forms", error.message);
      setHasForms(false);
    }
  };

  // Helper functions
  const formatFieldName = (fieldId) => {
    return fieldId.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).trim();
  };

  const formatCollectionName = (collId) => {
    return collId.charAt(0).toUpperCase() + collId.slice(1);
  };

  const inferFieldType = (value) => {
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    return 'string';
  };

  const mapFieldType = (formFieldType) => {
    const typeMap = {
      'text': 'string', 'email': 'string', 'password': 'string',
      'textarea': 'string', 'select': 'string', 'radio': 'string',
      'checkbox': 'boolean', 'number': 'number',
      'date': 'string', 'time': 'string'
    };
    return typeMap[formFieldType] || 'string';
  };

  // Rule Builder Functions
  const addCondition = () => {
    const newCondition = {
      id: currentRule.conditions.length + 1,
      logicalOperator: 'AND',
      collection: '',
      field: '',
      operator: '',
      value: ''
    };
    setCurrentRule({ ...currentRule, conditions: [...currentRule.conditions, newCondition] });
  };

  const updateCondition = (id, updates) => {
    setCurrentRule({
      ...currentRule,
      conditions: currentRule.conditions.map(c => c.id === id ? { ...c, ...updates } : c)
    });
  };

  const removeCondition = (id) => {
    if (currentRule.conditions.length > 1) {
      setCurrentRule({
        ...currentRule,
        conditions: currentRule.conditions.filter(c => c.id !== id)
      });
    }
  };

  const handleSaveRule = async () => {
    if (!currentRule.ruleName) {
      alert('Please provide a rule name');
      return;
    }

    if (!currentRule.errorMessage || !currentRule.errorMessage.trim()) {
      alert('Error message is required');
      return;
    }

    if (!appName) {
      alert('App name is required');
      return;
    }

    try {
      // Normalize appName to lowercase
      const normalizedAppName = appName.toLowerCase();
      
      logger.info("handleSaveRule", "Saving rule", { ruleName: currentRule.ruleName, editing: !!editingRule, normalizedAppName });

      const ruleData = {
        ...currentRule,
        id: currentRule.ruleId || `RULE_${Date.now()}`,
        name: currentRule.ruleName,
        type: currentRule.ruleCategory === 'VALIDATION' ? 'visibility' : 'business_rule',
        appName: normalizedAppName
      };

      if (editingRule) {
        await validationService.updateValidation(normalizedAppName, ruleData.id, ruleData, dbName);
        logger.info("handleSaveRule", "Rule updated", { ruleId: ruleData.id });
      } else {
        await validationService.createValidation(normalizedAppName, ruleData, dbName);
        logger.info("handleSaveRule", "Rule created", { ruleId: ruleData.id });
      }

      await loadRulesFromAPI();

      setCurrentRule({
        ruleId: '',
        ruleName: '',
        ruleCategory: 'AUTHORIZATION',
        description: '',
        conditions: [{ id: 1, logicalOperator: 'IF', collection: '', field: '', operator: '', value: '' }],
        status: 'ACTIVE',
        errorMessage: '',
        successMessage: '',
        warningMessage: '',
        fallbackText: '',
        fallbackComponent: '',
        hideSpace: false
      });
      setEditingRule(null);
      setView('library');

      alert('âœ… Rule saved successfully!');
    } catch (error) {
      logger.error("handleSaveRule", "Failed to save rule", error);
      alert(`âŒ Failed to save rule: ${error.message}`);
    }
  };

  const handleEditRule = (rule) => {
    setCurrentRule(rule);
    setEditingRule(rule);
    setView('builder');
  };

  const handleCloneRule = (rule) => {
    setCurrentRule({ ...rule, ruleId: '', ruleName: `${rule.ruleName} (Copy)` });
    setEditingRule(null);
    setView('builder');
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm('âš ï¸ Are you sure you want to delete this rule?')) {
      return;
    }

    try {
      // Normalize appName to lowercase
      const normalizedAppName = appName.toLowerCase();
      
      logger.info("deleteRule", "Deleting rule", { ruleId, normalizedAppName });
      await validationService.deleteValidation(normalizedAppName, ruleId, dbName);
      await loadRulesFromAPI();
      logger.info("deleteRule", "Rule deleted successfully");
      alert('âœ… Rule deleted successfully!');
    } catch (error) {
      logger.error("deleteRule", "Failed to delete rule", error);
      alert(`âŒ Failed to delete rule: ${error.message}`);
    }
  };

  const getFieldsByCollection = (collectionId) => {
    const collection = collections.find(c => c.id === collectionId);
    return collection ? collection.fields : [];
  };

  const getFieldType = (collectionId, fieldId) => {
    const fields = getFieldsByCollection(collectionId);
    const field = fields.find(f => f.id === fieldId);
    return field ? field.type : 'string';
  };

  // Fetch distinct values for a field from the collection
  // ========== TEMPLATE BUILDER FUNCTIONS (from default-validation-builder) ==========

  // Load saved template validations from Firestore
  useEffect(() => {
    loadTemplateValidations();
  }, [appName]);

  const loadTemplateValidations = async () => {
    if (!appName) return;
    
    try {
      // Normalize appName to lowercase
      const normalizedAppName = appName.toLowerCase();
      
      logger.info("loadTemplateValidations", "Fetching template validations", { appName, normalizedAppName });
      const data = await validationService.getAllValidations(normalizedAppName, dbName);
      
      // Filter only template-type validations (those with validationType field present)
      const templates = data.filter(v => v.validationType);
      
      const convertedTemplates = templates.map(v => ({
        id: v.id || v.ruleId,
        name: v.name || v.ruleName,
        type: v.validationType || 'ALWAYS_SHOW',
        config: v.validationConfig || v.config || {},
        errorMessage: v.errorMessage || '',
        successMessage: v.successMessage || '',
        warningMessage: v.warningMessage || '',
        createdAt: v.createdAt || new Date().toISOString()
      }));
      
      setSavedValidations(convertedTemplates);
      logger.info("loadTemplateValidations", "Templates loaded", { count: convertedTemplates.length });
    } catch (error) {
      logger.error("loadTemplateValidations", "Failed to load templates", error);
      setSavedValidations([]);
    }
  };

  // Handle validation type change
  const handleTypeChange = (type) => {
    setValidationType(type);
    setValidationConfig({});
  };

  // Handle config field change
  const handleConfigChange = (field, value) => {
    setValidationConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save validation to Firestore
  const saveValidation = async () => {
    if (!validationName.trim()) {
      alert('Please enter a validation name');
      return;
    }

    if (!templateMessages.errorMessage.trim()) {
      alert('Error message is required');
      return;
    }

    if (!appName) {
      alert('App name is required');
      return;
    }

    try {
      // Normalize appName to lowercase
      const normalizedAppName = appName.toLowerCase();
      
      logger.info("saveValidation", "Saving template validation", { name: validationName, type: validationType, normalizedAppName });

      const templateData = {
        id: `TEMPLATE_${Date.now()}`,
        name: validationName,
        ruleName: validationName,
        type: 'template',
        validationType: validationType,
        validationConfig: validationConfig,
        config: validationConfig,
        ruleCategory: 'VALIDATION',
        description: `Template validation: ${VALIDATION_TYPES[validationType]?.label}`,
        conditions: [],
        status: 'ACTIVE',
        errorMessage: templateMessages.errorMessage,
        successMessage: templateMessages.successMessage || '',
        warningMessage: templateMessages.warningMessage || '',
        createdAt: new Date().toISOString(),
        appName: normalizedAppName
      };

      await validationService.createValidation(normalizedAppName, templateData, dbName);
      logger.info("saveValidation", "Template saved successfully");

      // Reload templates
      await loadTemplateValidations();

      // Reset form
      setValidationName('');
      setValidationType('ALWAYS_SHOW');
      setValidationConfig({});
      setTemplateMessages({
        errorMessage: '',
        successMessage: '',
        warningMessage: ''
      });
      alert('âœ… Static template saved successfully to Firestore!');
    } catch (error) {
      logger.error("saveValidation", "Failed to save template", error);
      alert(`âŒ Failed to save template: ${error.message}`);
    }
  };

  // Delete validation from Firestore
  const deleteValidation = async (id) => {
    if (!window.confirm('âš ï¸ Are you sure you want to delete this template validation?')) {
      return;
    }

    try {
      // Normalize appName to lowercase
      const normalizedAppName = appName.toLowerCase();
      
      logger.info("deleteValidation", "Deleting template validation", { id, normalizedAppName });
      await validationService.deleteValidation(normalizedAppName, id, dbName);
      
      // Reload templates
      await loadTemplateValidations();
      
      logger.info("deleteValidation", "Template deleted successfully");
      alert('âœ… Template validation deleted successfully!');
    } catch (error) {
      logger.error("deleteValidation", "Failed to delete template", error);
      alert(`âŒ Failed to delete template: ${error.message}`);
    }
  };


  // Evaluate validation condition
  const evaluateValidation = (validation) => {
    const now = new Date();
    const { type, config } = validation;

    switch (type) {
      case 'ALWAYS_SHOW':
        return { result: true, message: 'Always shows - condition met' };
      
      case 'NEVER_SHOW':
        return { result: false, message: 'Never shows - condition not met' };
      
      case 'DATE_RANGE':
        if (!config.startDate || !config.endDate) return { result: false, message: 'Missing date configuration' };
        const start = new Date(config.startDate);
        const end = new Date(config.endDate);
        const inRange = now >= start && now <= end;
        return { result: inRange, message: inRange ? 'Current date is within range' : 'Current date is outside range' };
      
      case 'TIME_RANGE':
        if (!config.startTime || !config.endTime) return { result: false, message: 'Missing time configuration' };
        const currentTime = now.getHours() * 60 + now.getMinutes();
        const [startH, startM] = config.startTime.split(':').map(Number);
        const [endH, endM] = config.endTime.split(':').map(Number);
        const startMin = startH * 60 + startM;
        const endMin = endH * 60 + endM;
        const inTimeRange = currentTime >= startMin && currentTime <= endMin;
        return { result: inTimeRange, message: inTimeRange ? 'Current time is within range' : 'Current time is outside range' };
      
      case 'WEEKDAY_ONLY':
        const isWeekday = now.getDay() >= 1 && now.getDay() <= 5;
        return { result: isWeekday, message: isWeekday ? 'Today is a weekday' : 'Today is not a weekday' };
      
      case 'WEEKEND_ONLY':
        const isWeekend = now.getDay() === 0 || now.getDay() === 6;
        return { result: isWeekend, message: isWeekend ? 'Today is weekend' : 'Today is not weekend' };
      
      case 'MOBILE_DEVICE':
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        return { result: isMobile, message: isMobile ? 'Mobile device detected' : 'Not a mobile device' };
      
      case 'DESKTOP_DEVICE':
        const isDesktop = !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        return { result: isDesktop, message: isDesktop ? 'Desktop device detected' : 'Not a desktop device' };
      
      case 'SPECIFIC_DAY':
        if (!config.day) return { result: false, message: 'No day configured' };
        const currentDay = DAYS[now.getDay() === 0 ? 6 : now.getDay() - 1];
        const matchesDay = currentDay === config.day;
        return { result: matchesDay, message: matchesDay ? `Today is ${config.day}` : `Today is not ${config.day}` };
      
      case 'BEFORE_DATE':
        if (!config.cutoffDate) return { result: false, message: 'No cutoff date configured' };
        const cutoff = new Date(config.cutoffDate);
        const beforeDate = now < cutoff;
        return { result: beforeDate, message: beforeDate ? 'Current date is before cutoff' : 'Current date is after cutoff' };
      
      case 'AFTER_DATE':
        if (!config.startDate) return { result: false, message: 'No start date configured' };
        const afterStart = new Date(config.startDate);
        const afterDate = now > afterStart;
        return { result: afterDate, message: afterDate ? 'Current date is after start date' : 'Current date is before start date' };
      
      case 'BUSINESS_HOURS':
        const day = now.getDay();
        const hour = now.getHours();
        const isBusinessHours = day >= 1 && day <= 5 && hour >= 9 && hour < 17;
        return { result: isBusinessHours, message: isBusinessHours ? 'Within business hours' : 'Outside business hours' };
      
      case 'SPECIFIC_MONTH':
        if (!config.month) return { result: false, message: 'No month configured' };
        const currentMonth = MONTHS[now.getMonth()];
        const matchesMonth = currentMonth === config.month;
        return { result: matchesMonth, message: matchesMonth ? `Current month is ${config.month}` : `Current month is not ${config.month}` };
      
      case 'SEASON':
        if (!config.season) return { result: false, message: 'No season configured' };
        const month = now.getMonth();
        let currentSeason = '';
        if (month >= 2 && month <= 4) currentSeason = 'Spring';
        else if (month >= 5 && month <= 7) currentSeason = 'Summer';
        else if (month >= 8 && month <= 10) currentSeason = 'Fall';
        else currentSeason = 'Winter';
        const matchesSeason = currentSeason === config.season;
        return { result: matchesSeason, message: matchesSeason ? `Current season is ${config.season}` : `Current season is ${currentSeason}, not ${config.season}` };
      
      case 'BROWSER_TYPE':
        if (!config.browser) return { result: false, message: 'No browser configured' };
        const userAgent = navigator.userAgent;
        let detectedBrowser = '';
        if (userAgent.includes('Chrome') && !userAgent.includes('Edge')) detectedBrowser = 'Chrome';
        else if (userAgent.includes('Firefox')) detectedBrowser = 'Firefox';
        else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) detectedBrowser = 'Safari';
        else if (userAgent.includes('Edge')) detectedBrowser = 'Edge';
        else if (userAgent.includes('Opera')) detectedBrowser = 'Opera';
        const matchesBrowser = detectedBrowser === config.browser;
        return { result: matchesBrowser, message: matchesBrowser ? `Browser is ${config.browser}` : `Browser is ${detectedBrowser}, not ${config.browser}` };
      
      case 'SCREEN_WIDTH':
        const width = window.innerWidth;
        const minOk = !config.minWidth || width >= parseInt(config.minWidth);
        const maxOk = !config.maxWidth || width <= parseInt(config.maxWidth);
        const widthOk = minOk && maxOk;
        return { result: widthOk, message: widthOk ? `Screen width ${width}px is within range` : `Screen width ${width}px is outside range` };
      
      case 'FEATURE_FLAG':
        if (!config.flagName) return { result: false, message: 'No flag name configured' };
        const flagEnabled = config.enabled === 'true' || config.enabled === true;
        return { result: flagEnabled, message: flagEnabled ? `Feature flag "${config.flagName}" is enabled` : `Feature flag "${config.flagName}" is disabled` };
      
      default:
        return { result: false, message: 'Unknown validation type' };
    }
  };

  // Test selected validation
  const testValidation = () => {
    const validation = savedValidations.find(v => v.id === selectedValidationId);
    if (!validation) {
      alert('Please select a validation to test');
      return;
    }
    
    const evalResult = evaluateValidation(validation);
    
    // Include messages based on the result
    const result = {
      ...evalResult,
      validation: validation,
      displayMessage: evalResult.result 
        ? (validation.successMessage || 'Validation passed') 
        : (validation.errorMessage || 'Validation failed'),
      warningMessage: validation.warningMessage || ''
    };
    
    setPreviewResult(result);
  };

  // Test business rule with sample data
  const testBusinessRule = () => {
    if (!currentRule.conditions || currentRule.conditions.length === 0) {
      alert('Please add at least one condition to test');
      return;
    }

    // Check if all conditions have required test data
    for (const condition of currentRule.conditions) {
      if (condition.collection && condition.field && !testData[`${condition.collection}.${condition.field}`]) {
        alert(`Please provide test data for ${condition.collection}.${condition.field}`);
        return;
      }
    }

    // Evaluate each condition
    let evaluationResults = [];
    for (const condition of currentRule.conditions) {
      if (condition.logicalOperator === 'IF' || condition.logicalOperator === 'AND' || condition.logicalOperator === 'OR') {
        const fieldKey = `${condition.collection}.${condition.field}`;
        const testValue = testData[fieldKey];
        const conditionValue = condition.value;
        
        let result = false;
        switch (condition.operator) {
          case 'EQUALS':
            result = testValue == conditionValue;
            break;
          case 'NOT_EQUALS':
            result = testValue != conditionValue;
            break;
          case 'GREATER_THAN':
            result = parseFloat(testValue) > parseFloat(conditionValue);
            break;
          case 'LESS_THAN':
            result = parseFloat(testValue) < parseFloat(conditionValue);
            break;
          case 'GREATER_OR_EQUAL':
            result = parseFloat(testValue) >= parseFloat(conditionValue);
            break;
          case 'LESS_OR_EQUAL':
            result = parseFloat(testValue) <= parseFloat(conditionValue);
            break;
          case 'CONTAINS':
            result = String(testValue).includes(String(conditionValue));
            break;
          case 'IN':
            const values = conditionValue.split(',').map(v => v.trim());
            result = values.includes(String(testValue));
            break;
          case 'IS_TRUE':
            result = testValue === true || testValue === 'true';
            break;
          case 'IS_FALSE':
            result = testValue === false || testValue === 'false';
            break;
          default:
            result = false;
        }
        
        evaluationResults.push({
          condition: `${condition.collection}.${condition.field} ${condition.operator} ${conditionValue}`,
          result: result,
          operator: condition.logicalOperator
        });
      }
    }

    // Combine results based on logical operators (simplified AND/OR logic)
    let finalResult = evaluationResults.length > 0 ? evaluationResults[0].result : false;
    for (let i = 1; i < evaluationResults.length; i++) {
      if (evaluationResults[i-1].operator === 'AND') {
        finalResult = finalResult && evaluationResults[i].result;
      } else if (evaluationResults[i-1].operator === 'OR') {
        finalResult = finalResult || evaluationResults[i].result;
      }
    }

    setBusinessRuleTestResult({
      result: finalResult,
      evaluations: evaluationResults,
      displayMessage: finalResult 
        ? (currentRule.successMessage || 'Business rule passed - Component will be shown') 
        : (currentRule.errorMessage || 'Business rule failed - Component will be hidden'),
      warningMessage: currentRule.warningMessage || '',
      errorMessage: currentRule.errorMessage || '',
      successMessage: currentRule.successMessage || ''
    });
  };

  // Render form fields based on validation type
  const renderConfigFields = () => {
    const fields = VALIDATION_TYPES[validationType]?.fields || [];
    
    if (fields.length === 0) {
      return <p style={{ fontSize: '13px', color: '#6B7280' }}>No additional configuration required</p>;
    }

    return fields.map(field => {
      switch (field) {
        case 'startDate':
        case 'endDate':
        case 'cutoffDate':
          return (
            <div key={field} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                {field.replace(/([A-Z])/g, ' $1').trim()}
              </label>
              <input
                type="date"
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              />
            </div>
          );
        
        case 'startTime':
        case 'endTime':
          return (
            <div key={field} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                {field.replace(/([A-Z])/g, ' $1').trim()}
              </label>
              <input
                type="time"
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              />
            </div>
          );
        
        case 'day':
          return (
            <div key={field} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Day of Week</label>
              <select
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              >
                <option value="">Select Day</option>
                {DAYS.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
          );
        
        case 'month':
          return (
            <div key={field} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Month</label>
              <select
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              >
                <option value="">Select Month</option>
                {MONTHS.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
          );
        
        case 'season':
          return (
            <div key={field} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Season</label>
              <select
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              >
                <option value="">Select Season</option>
                {SEASONS.map(season => (
                  <option key={season} value={season}>{season}</option>
                ))}
              </select>
            </div>
          );
        
        case 'browser':
          return (
            <div key={field} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Browser</label>
              <select
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              >
                <option value="">Select Browser</option>
                {BROWSERS.map(browser => (
                  <option key={browser} value={browser}>{browser}</option>
                ))}
              </select>
            </div>
          );
        
        case 'minWidth':
        case 'maxWidth':
          return (
            <div key={field} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>
                {field.replace(/([A-Z])/g, ' $1').trim()} (px)
              </label>
              <input
                type="number"
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                placeholder="e.g. 768"
              />
            </div>
          );
        
        case 'flagName':
          return (
            <div key={field} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Feature Flag Name</label>
              <input
                type="text"
                value={validationConfig[field] || ''}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
                placeholder="e.g. new_feature_enabled"
              />
            </div>
          );
        
        case 'enabled':
          return (
            <div key={field} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Status</label>
              <select
                value={validationConfig[field] || 'false'}
                onChange={(e) => handleConfigChange(field, e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px' }}
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>
          );
        
        default:
          return null;
      }
    });
  };

  // ========== END TEMPLATE BUILDER FUNCTIONS ==========

  // Render Functions
  const renderRuleLibrary = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0' }}>
            Business Rules Library
          </h1>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>
            Create once, apply everywhere ðŸš€ | App: {appName}
          </p>
        </div>
      </div>

      {/* Loading State */}
      {loadingRules ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '4px solid #E5E7EB',
            borderTop: '4px solid #3B82F6',
            borderRadius: '50%',
            margin: '0 auto 16px auto',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading rules...
        </div>
      ) : (
        <>
          {/* Collection Warning */}
          {!loadingCollections && collections.length === 0 && (
            <div style={{
              backgroundColor: '#FEF3C7',
              border: '1px solid #FCD34D',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ color: '#92400E', fontSize: '14px', margin: 0 }}>
                âš ï¸ No data collections found. Please create {appName}_users, {appName}_transactions, or add forms with fields.
              </p>
            </div>
          )}

          {/* Category Summary */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
            {RULE_CATEGORIES.map(cat => {
              // For STATIC category, count savedValidations, otherwise count from rules
              const count = cat.id === 'STATIC' 
                ? savedValidations.length 
                : rules.filter(r => r.ruleCategory === cat.id).length;
              return (
                <div 
                  key={cat.id} 
                  style={{
                    backgroundColor: cat.bgColor,
                    padding: '16px 20px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    minWidth: '180px'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>{cat.icon}</span>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: cat.textColor }}>
                      {cat.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                      {count} {count === 1 ? 'rule' : 'rules'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ACCORDION 1: Static Validations */}
          <div style={{ 
            marginBottom: '24px', 
            backgroundColor: 'white', 
            border: '2px solid #E5E7EB', 
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}>
            <div 
              onClick={() => setShowTemplateAccordion(!showTemplateAccordion)}
              style={{
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                backgroundColor: showTemplateAccordion ? '#F0F9FF' : 'white',
                borderRadius: '12px',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>âš¡</span>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0 }}>
                    Static Validations
                  </h3>
                  <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0 0' }}>
                    17 pre-built templates (date, time, device, browser) â€¢ {savedValidations.length} saved â€¢ No data collections required
                  </p>
                </div>
              </div>
              <span style={{ 
                fontSize: '20px', 
                transform: showTemplateAccordion ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }}>
                â–¼
              </span>
            </div>

            {showTemplateAccordion && (
              <div style={{ padding: '20px', borderTop: '1px solid #E5E7EB' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  {/* Left: Create Template */}
                  <div style={{ 
                    backgroundColor: '#F9FAFB', 
                    borderRadius: '8px', 
                    padding: '20px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <h4 style={{ 
                      fontSize: '15px', 
                      fontWeight: '700', 
                      color: '#374151', 
                      margin: '0 0 16px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>âž•</span> Create Validation Template
                    </h4>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                        Template Name
                      </label>
                      <input
                        type="text"
                        value={validationName}
                        onChange={(e) => setValidationName(e.target.value)}
                        placeholder="e.g., Show on Mobile Only"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '13px'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                        Validation Type
                      </label>
                      <select
                        value={validationType}
                        onChange={(e) => handleTypeChange(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '13px'
                        }}
                      >
                        {Object.entries(VALIDATION_TYPES).map(([key, val]) => (
                          <option key={key} value={key}>{val.label}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{ 
                      backgroundColor: 'white', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginBottom: '12px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <h5 style={{ fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                        Configuration
                      </h5>
                      {renderConfigFields()}
                    </div>

                    {/* Message Fields */}
                    <div style={{ 
                      backgroundColor: 'white', 
                      padding: '12px', 
                      borderRadius: '6px',
                      marginBottom: '12px',
                      border: '1px solid #E5E7EB'
                    }}>
                      <h5 style={{ fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                        Messages
                      </h5>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#DC2626', marginBottom: '4px' }}>
                          âŒ Error Message *
                        </label>
                        <input
                          type="text"
                          value={templateMessages.errorMessage}
                          onChange={(e) => setTemplateMessages({ ...templateMessages, errorMessage: e.target.value })}
                          placeholder="Message shown when validation fails"
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '12px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#059669', marginBottom: '4px' }}>
                          âœ… Success Message
                        </label>
                        <input
                          type="text"
                          value={templateMessages.successMessage}
                          onChange={(e) => setTemplateMessages({ ...templateMessages, successMessage: e.target.value })}
                          placeholder="Message shown when validation passes (optional)"
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '12px'
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#D97706', marginBottom: '4px' }}>
                          âš ï¸ Warning Message
                        </label>
                        <input
                          type="text"
                          value={templateMessages.warningMessage}
                          onChange={(e) => setTemplateMessages({ ...templateMessages, warningMessage: e.target.value })}
                          placeholder="Optional warning message (optional)"
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            fontSize: '12px'
                          }}
                        />
                      </div>
                    </div>

                    <button
                      onClick={saveValidation}
                      style={{
                        width: '100%',
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        padding: '10px',
                        borderRadius: '6px',
                        border: 'none',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <span>ðŸ’¾</span> Save Template
                    </button>
                  </div>

                  {/* Right: Saved Templates */}
                  <div style={{ 
                    backgroundColor: '#F9FAFB', 
                    borderRadius: '8px', 
                    padding: '20px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <h4 style={{ 
                      fontSize: '15px', 
                      fontWeight: '700', 
                      color: '#374151', 
                      margin: '0 0 16px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>ðŸ“š</span> Saved Templates ({savedValidations.length})
                    </h4>
                    
                    <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
                      {savedValidations.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#9CA3AF', fontSize: '13px', padding: '20px 0' }}>
                          No templates saved yet
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {savedValidations.map(validation => (
                            <div 
                              key={validation.id} 
                              style={{
                                backgroundColor: 'white',
                                border: '1px solid #E5E7EB',
                                borderRadius: '6px',
                                padding: '12px'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                                <div>
                                  <h5 style={{ fontSize: '13px', fontWeight: '600', color: '#111827', margin: '0 0 4px 0' }}>
                                    {validation.name}
                                  </h5>
                                  <p style={{ fontSize: '11px', color: '#6B7280', margin: 0 }}>
                                    {VALIDATION_TYPES[validation.type]?.label || validation.type || 'Unknown Type'}
                                  </p>
                                </div>
                                <button
                                  onClick={() => deleteValidation(validation.id)}
                                  style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: '#EF4444',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    padding: '4px'
                                  }}
                                  title="Delete"
                                >
                                  ðŸ—‘ï¸
                                </button>
                              </div>
                              
                              {validation.config && Object.keys(validation.config).length > 0 && (
                                <div style={{ 
                                  backgroundColor: '#F9FAFB', 
                                  padding: '8px', 
                                  borderRadius: '4px', 
                                  fontSize: '10px',
                                  fontFamily: 'monospace',
                                  marginBottom: '8px',
                                  maxHeight: '60px',
                                  overflowY: 'auto'
                                }}>
                                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                    {JSON.stringify(validation.config, null, 2)}
                                  </pre>
                                </div>
                              )}
                              
                              {/* Show messages if available */}
                              {(validation.errorMessage || validation.successMessage || validation.warningMessage) && (
                                <div style={{ 
                                  backgroundColor: '#F9FAFB', 
                                  padding: '8px', 
                                  borderRadius: '4px', 
                                  fontSize: '11px',
                                  marginBottom: '8px'
                                }}>
                                  {validation.errorMessage && (
                                    <div style={{ color: '#DC2626', marginBottom: '4px' }}>
                                      âŒ {validation.errorMessage}
                                    </div>
                                  )}
                                  {validation.successMessage && (
                                    <div style={{ color: '#059669', marginBottom: '4px' }}>
                                      âœ… {validation.successMessage}
                                    </div>
                                  )}
                                  {validation.warningMessage && (
                                    <div style={{ color: '#D97706' }}>
                                      âš ï¸ {validation.warningMessage}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bottom Section: Enhanced Testing */}
                <div style={{ 
                  backgroundColor: '#FEF3C7', 
                  borderRadius: '8px', 
                  padding: '20px',
                  border: '1px solid #FCD34D'
                }}>
                  <h4 style={{ 
                    fontSize: '15px', 
                    fontWeight: '700', 
                    color: '#92400E', 
                    margin: '0 0 16px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>ðŸ§ª</span> Test Validation with Sample Data
                  </h4>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#92400E', marginBottom: '6px' }}>
                      Select Template to Test
                    </label>
                    <select
                      value={selectedValidationId}
                      onChange={(e) => setSelectedValidationId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #FCD34D',
                        borderRadius: '6px',
                        fontSize: '13px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Choose a template...</option>
                      {savedValidations.map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={testValidation}
                    style={{
                      width: '100%',
                      backgroundColor: '#8B5CF6',
                      color: 'white',
                      padding: '10px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginBottom: '12px'
                    }}
                  >
                    â–¶ï¸ Run Test
                  </button>

                  {previewResult && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* Validation Result */}
                      <div style={{
                        padding: '16px',
                        borderRadius: '6px',
                        backgroundColor: previewResult.result ? '#D1FAE5' : '#FEE2E2',
                        border: `2px solid ${previewResult.result ? '#10B981' : '#EF4444'}`
                      }}>
                        <h5 style={{ 
                          fontSize: '14px', 
                          fontWeight: '700', 
                          color: previewResult.result ? '#065F46' : '#991B1B',
                          margin: '0 0 8px 0' 
                        }}>
                          {previewResult.result ? 'âœ… Validation Passed (Show Component)' : 'âŒ Validation Failed (Hide Component)'}
                        </h5>
                        <p style={{ 
                          fontSize: '12px', 
                          color: '#6B7280',
                          margin: '0 0 8px 0',
                          lineHeight: '1.5'
                        }}>
                          <strong>Test Result:</strong> {previewResult.message}
                        </p>
                      </div>
                      
                      {/* Display Messages */}
                      <div style={{
                        padding: '16px',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        border: '1px solid #E5E7EB'
                      }}>
                        <h6 style={{ 
                          fontSize: '13px', 
                          fontWeight: '700', 
                          color: '#374151',
                          margin: '0 0 12px 0' 
                        }}>
                          ðŸ“¢ Messages that will be displayed:
                        </h6>
                        
                        {previewResult.displayMessage && (
                          <div style={{ 
                            padding: '8px 12px',
                            borderRadius: '4px',
                            backgroundColor: previewResult.result ? '#D1FAE5' : '#FEE2E2',
                            color: previewResult.result ? '#065F46' : '#991B1B',
                            fontSize: '12px',
                            marginBottom: '8px',
                            fontWeight: '600'
                          }}>
                            {previewResult.result ? 'âœ…' : 'âŒ'} {previewResult.displayMessage}
                          </div>
                        )}
                        
                        {previewResult.warningMessage && (
                          <div style={{ 
                            padding: '8px 12px',
                            borderRadius: '4px',
                            backgroundColor: '#FEF3C7',
                            color: '#92400E',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            âš ï¸ {previewResult.warningMessage}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ACCORDION 2: Form Validations (Only visible if app has forms) */}
          {hasForms && (
            <div style={{ 
              marginBottom: '24px', 
              backgroundColor: 'white', 
              border: '2px solid #E5E7EB', 
              borderRadius: '12px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}>
              <div 
                onClick={() => setShowFormValidationsAccordion(!showFormValidationsAccordion)}
                style={{
                  padding: '16px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  backgroundColor: showFormValidationsAccordion ? '#F0F9FF' : 'white',
                  borderRadius: '12px',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>ðŸ“</span>
                  <div>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111827', margin: 0 }}>
                      Form Validations
                    </h3>
                    <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0 0' }}>
                      Field-level validation rules for form components
                    </p>
                  </div>
                </div>
                <span style={{ 
                  fontSize: '20px', 
                  transform: showFormValidationsAccordion ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}>
                  â–¼
                </span>
              </div>

              {showFormValidationsAccordion && (
                <div style={{ padding: '40px 20px', borderTop: '1px solid #E5E7EB', textAlign: 'center', backgroundColor: 'white' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>ðŸš§</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Coming Soon
                  </div>
                  <div style={{ fontSize: '14px', color: '#6B7280', maxWidth: '500px', margin: '0 auto' }}>
                    Form-specific validation rules are under development. You'll soon be able to create field-level validations like required, email format, min/max length, etc.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SECTION: Business Rules (Main Grid) */}
          <div style={{ marginBottom: '16px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>
              ðŸ’¼ Business Rules Library
            </h2>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px' }}>
              Data-driven rules with collection conditions â€¢ {collections.length > 0 ? `${collections.length} collections available` : 'No data collections present'}
            </p>
          </div>

          {/* Rules Grid */}
          {rules.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '2px dashed #D1D5DB'
            }}>
              <p style={{ fontSize: '16px', fontWeight: '500', color: '#6B7280', margin: '0 0 16px 0' }}>
                No rules created yet
              </p>
              <button
                onClick={() => setView('builder')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <span style={{ fontSize: '20px' }}>+</span>
                Create Your First Rule
              </button>
            </div>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '20px' 
            }}>
              {rules.map(rule => {
                const category = RULE_CATEGORIES.find(c => c.id === rule.ruleCategory);
                return (
                  <div 
                    key={rule.ruleId} 
                    style={{
                      backgroundColor: 'white',
                      border: '2px solid #E5E7EB',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.15)';
                      e.currentTarget.style.borderColor = '#3B82F6';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{
                        backgroundColor: category?.bgColor || '#F3F4F6',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: category?.textColor || '#374151'
                      }}>
                        <span>{category?.icon}</span>
                        {category?.name}
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button 
                          onClick={() => handleEditRule(rule)} 
                          style={{
                            padding: '6px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            color: '#3B82F6'
                          }}
                          title="Edit rule"
                        >
                          âœï¸
                        </button>
                        <button 
                          onClick={() => handleCloneRule(rule)}
                          style={{
                            padding: '6px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            color: '#10B981'
                          }}
                          title="Clone rule"
                        >
                          ðŸ“‹
                        </button>
                        <button 
                          onClick={() => deleteRule(rule.ruleId)}
                          style={{
                            padding: '6px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            color: '#EF4444'
                          }}
                          title="Delete rule"
                        >
                          ðŸ—‘ï¸
                        </button>
                      </div>
                    </div>

                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: '0 0 8px 0' }}>
                      {rule.ruleName}
                    </h3>
                    <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 12px 0', lineHeight: '1.5' }}>
                      {rule.description || 'No description provided'}
                    </p>

                    <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '12px' }}>
                      {rule.conditions?.length || 0} condition{(rule.conditions?.length || 0) !== 1 ? 's' : ''}
                    </div>

                    {rule.usageCount > 0 && (
                      <div style={{ paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
                        <div style={{ fontSize: '11px', color: '#9CA3AF', marginBottom: '4px' }}>
                          Used in {rule.usageCount} components
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderRuleBuilder = () => (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: 0 }}>
          {editingRule ? 'Edit Rule' : 'Create New Rule'}
        </h1>
        <button
          onClick={() => {
            setView('library');
            setEditingRule(null);
            setCurrentRule({
              ruleId: '',
              ruleName: '',
              ruleCategory: 'AUTHORIZATION',
              description: '',
              conditions: [{ id: 1, logicalOperator: 'IF', collection: '', field: '', operator: '', value: '' }],
              status: 'ACTIVE',
              errorMessage: '',
              successMessage: '',
              warningMessage: '',
              fallbackText: '',
              fallbackComponent: '',
              hideSpace: false
            });
          }}
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            color: '#6B7280',
            backgroundColor: 'white',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          â† Back to Library
        </button>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        padding: '32px'
      }}>
        {/* Basic Info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#374151', 
              marginBottom: '8px' 
            }}>
              Rule Name *
            </label>
            <input
              type="text"
              value={currentRule.ruleName}
              onChange={(e) => setCurrentRule({ ...currentRule, ruleName: e.target.value })}
              placeholder="e.g., Premium Customer Access"
              style={{
                width: '100%',
                border: '2px solid #D1D5DB',
                borderRadius: '8px',
                padding: '10px 12px',
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
              onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#374151', 
              marginBottom: '8px' 
            }}>
              Category *
            </label>
            <select
              value={currentRule.ruleCategory}
              onChange={(e) => setCurrentRule({ ...currentRule, ruleCategory: e.target.value })}
              style={{
                width: '100%',
                border: '2px solid #D1D5DB',
                borderRadius: '8px',
                padding: '10px 12px',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'white'
              }}
            >
              {RULE_CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '8px' 
          }}>
            Description
          </label>
          <textarea
            value={currentRule.description}
            onChange={(e) => setCurrentRule({ ...currentRule, description: e.target.value })}
            placeholder="Describe what this rule does..."
            style={{
              width: '100%',
              border: '2px solid #D1D5DB',
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '14px',
              outline: 'none',
              fontFamily: 'inherit',
              resize: 'vertical',
              minHeight: '60px'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3B82F6'}
            onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
          />
        </div>

        {/* Collections Status */}
        {!loadingCollections && (
          <div style={{
            backgroundColor: collections.length === 0 ? '#FEF3C7' : '#EFF6FF',
            border: `1px solid ${collections.length === 0 ? '#FCD34D' : '#BFDBFE'}`,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {collections.length === 0 ? (
                <p style={{ color: '#92400E', fontSize: '13px', margin: 0 }}>
                  âš ï¸ No data tables found. Please create tables in <strong>Data Management</strong> first.
                </p>
              ) : (
                <p style={{ color: '#1E40AF', fontSize: '13px', margin: 0, fontWeight: '600' }}>
                  âœ… {collections.length} table{collections.length !== 1 ? 's' : ''} available: {collections.map(c => c.name).join(', ')}
                </p>
              )}
              
              <button
                type="button"
                onClick={() => {
                  // alert(`ðŸ”„ DEBUG: Manual refresh clicked\n\nCurrent collections: ${collections.length}\nCollections: ${JSON.stringify(collections.map(c => c.name))}`);
                  loadAvailableCollections();
                }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Refresh tables"
              >
                ðŸ”„ Refresh
              </button>
            </div>
          </div>
        )}

        {/* Conditions */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '12px' 
          }}>
            Conditions
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {currentRule.conditions.map((condition, index) => (
              <div 
                key={condition.id} 
                style={{
                  background: 'linear-gradient(135deg, #F9FAFB 0%, #EFF6FF 100%)',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '2px solid #E5E7EB'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  {/* Logical Operator */}
                  <div style={{ minWidth: '80px' }}>
                    {index === 0 ? (
                      <div style={{
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        textAlign: 'center',
                        fontWeight: '700',
                        fontSize: '13px'
                      }}>
                        IF
                      </div>
                    ) : (
                      <select
                        value={condition.logicalOperator}
                        onChange={(e) => updateCondition(condition.id, { logicalOperator: e.target.value })}
                        style={{
                          width: '100%',
                          border: '2px solid #D1D5DB',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          fontSize: '13px',
                          fontWeight: '700',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </select>
                    )}
                  </div>

                  {/* Condition Fields */}
                  <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px' }}>
                    <select
                      value={condition.collection}
                      onChange={(e) => {
                        const selectedCollection = e.target.value;
                        updateCondition(condition.id, { 
                          collection: selectedCollection, 
                          field: '', 
                          operator: '', 
                          value: '' 
                        });
                      }}
                      style={{
                        border: '2px solid #D1D5DB',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        fontSize: '13px',
                        backgroundColor: 'white'
                      }}
                      disabled={collections.length === 0}
                    >
                      <option value="">Select Table</option>
                      {collections.map(col => (
                        <option key={col.id} value={col.id}>{col.name}</option>
                      ))}
                    </select>

                    <select
                      value={condition.field}
                      onChange={(e) => {
                        const selectedField = e.target.value;
                        updateCondition(condition.id, { 
                          field: selectedField, 
                          operator: '', 
                          value: '' 
                        });
                      }}
                      disabled={!condition.collection}
                      style={{
                        border: '2px solid #D1D5DB',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        fontSize: '13px',
                        backgroundColor: condition.collection ? 'white' : '#F3F4F6',
                        cursor: condition.collection ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <option value="">Select Field</option>
                      {getFieldsByCollection(condition.collection).map(field => (
                        <option key={field.id} value={field.id}>{field.name}</option>
                      ))}
                    </select>

                    <select
                      value={condition.operator}
                      onChange={(e) => updateCondition(condition.id, { operator: e.target.value })}
                      disabled={!condition.field}
                      style={{
                        border: '2px solid #D1D5DB',
                        borderRadius: '6px',
                        padding: '8px 10px',
                        fontSize: '13px',
                        backgroundColor: condition.field ? 'white' : '#F3F4F6',
                        cursor: condition.field ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <option value="">Operator</option>
                      {condition.field && OPERATORS[getFieldType(condition.collection, condition.field)]?.map(op => (
                        <option key={op} value={op}>{op.replace(/_/g, ' ')}</option>
                      ))}
                    </select>

                    {getFieldType(condition.collection, condition.field) === 'boolean' ? (
                      <select
                        value={condition.value}
                        onChange={(e) => updateCondition(condition.id, { value: e.target.value === 'true' })}
                        style={{
                          border: '2px solid #D1D5DB',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          fontSize: '13px',
                          backgroundColor: 'white'
                        }}
                      >
                        <option value="">Select</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    ) : (
                      <input
                        type={getFieldType(condition.collection, condition.field) === 'number' ? 'number' : 'text'}
                        value={condition.value}
                        onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                        placeholder="Value"
                        style={{
                          border: '2px solid #D1D5DB',
                          borderRadius: '6px',
                          padding: '8px 10px',
                          fontSize: '13px'
                        }}
                      />
                    )}
                  </div>

                  {/* Delete Button */}
                  {currentRule.conditions.length > 1 && (
                    <button
                      onClick={() => removeCondition(condition.id)}
                      style={{
                        padding: '8px 10px',
                        backgroundColor: '#FEE2E2',
                        border: '1px solid #FCA5A5',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        color: '#DC2626',
                        fontSize: '16px'
                      }}
                      title="Remove condition"
                    >
                      ðŸ—‘ï¸
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={addCondition}
            style={{
              marginTop: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#3B82F6',
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: '20px' }}>+</span>
            Add Another Condition
          </button>
        </div>

        {/* Messages Section */}
        <div style={{ marginBottom: '24px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
            ðŸ“¢ Validation Messages
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#DC2626', marginBottom: '6px' }}>
                âŒ Error Message *
              </label>
              <input
                type="text"
                value={currentRule.errorMessage}
                onChange={(e) => setCurrentRule({ ...currentRule, errorMessage: e.target.value })}
                placeholder="e.g., You must be 18 or older"
                style={{
                  width: '100%',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '13px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#059669', marginBottom: '6px' }}>
                âœ… Success Message (Optional)
              </label>
              <input
                type="text"
                value={currentRule.successMessage}
                onChange={(e) => setCurrentRule({ ...currentRule, successMessage: e.target.value })}
                placeholder="e.g., Verification passed"
                style={{
                  width: '100%',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '13px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#D97706', marginBottom: '6px' }}>
                âš ï¸ Warning Message (Optional)
              </label>
              <input
                type="text"
                value={currentRule.warningMessage}
                onChange={(e) => setCurrentRule({ ...currentRule, warningMessage: e.target.value })}
                placeholder="e.g., Please verify your information"
                style={{
                  width: '100%',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '13px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Fallback Section */}
        <div style={{ marginBottom: '24px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
            ðŸ”„ Fallback Configuration (Optional)
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Fallback Text
              </label>
              <input
                type="text"
                value={currentRule.fallbackText}
                onChange={(e) => setCurrentRule({ ...currentRule, fallbackText: e.target.value })}
                placeholder="Text to show when hidden"
                style={{
                  width: '100%',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '13px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Fallback Component ID
              </label>
              <input
                type="text"
                value={currentRule.fallbackComponent}
                onChange={(e) => setCurrentRule({ ...currentRule, fallbackComponent: e.target.value })}
                placeholder="Alternative component ID"
                style={{
                  width: '100%',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '13px'
                }}
              />
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={currentRule.hideSpace}
                onChange={(e) => setCurrentRule({ ...currentRule, hideSpace: e.target.checked })}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                Hide Space (Collapse space when component is hidden)
              </span>
            </label>
          </div>
        </div>

        {/* Test Business Rule Section */}
        <div style={{ marginBottom: '24px', paddingTop: '24px', borderTop: '1px solid #E5E7EB' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '16px' }}>
            ðŸ§ª Test Business Rule with Sample Data
          </h3>
          
          <div style={{ backgroundColor: '#F9FAFB', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
              Enter Test Values for Each Field
            </h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {currentRule.conditions.map((condition, index) => {
                if (condition.collection && condition.field) {
                  const fieldKey = `${condition.collection}.${condition.field}`;
                  const fieldType = getFieldType(condition.collection, condition.field);
                  
                  return (
                    <div key={index}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                        {condition.collection}.{condition.field}
                      </label>
                      {fieldType === 'boolean' ? (
                        <select
                          value={testData[fieldKey] || ''}
                          onChange={(e) => setTestData({ ...testData, [fieldKey]: e.target.value === 'true' })}
                          style={{
                            width: '100%',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            fontSize: '13px',
                            backgroundColor: 'white'
                          }}
                        >
                          <option value="">Select value...</option>
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </select>
                      ) : (
                        <input
                          type={fieldType === 'number' ? 'number' : 'text'}
                          value={testData[fieldKey] || ''}
                          onChange={(e) => setTestData({ ...testData, [fieldKey]: e.target.value })}
                          placeholder={`Enter ${fieldType} value`}
                          style={{
                            width: '100%',
                            border: '1px solid #D1D5DB',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            fontSize: '13px'
                          }}
                        />
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
            
            <button
              onClick={testBusinessRule}
              style={{
                marginTop: '16px',
                width: '100%',
                backgroundColor: '#8B5CF6',
                color: 'white',
                padding: '10px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              â–¶ï¸ Run Test
            </button>
          </div>

          {/* Test Results */}
          {businessRuleTestResult && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Overall Result */}
              <div style={{
                padding: '16px',
                borderRadius: '6px',
                backgroundColor: businessRuleTestResult.result ? '#D1FAE5' : '#FEE2E2',
                border: `2px solid ${businessRuleTestResult.result ? '#10B981' : '#EF4444'}`
              }}>
                <h5 style={{ 
                  fontSize: '14px', 
                  fontWeight: '700', 
                  color: businessRuleTestResult.result ? '#065F46' : '#991B1B',
                  margin: '0 0 8px 0' 
                }}>
                  {businessRuleTestResult.result ? 'âœ… Rule Passed (Show Component)' : 'âŒ Rule Failed (Hide Component)'}
                </h5>
                
                {/* Show individual condition evaluations */}
                <div style={{ marginTop: '12px', fontSize: '12px' }}>
                  {businessRuleTestResult.evaluations.map((evaluation, idx) => (
                    <div key={idx} style={{ 
                      padding: '6px 8px',
                      marginBottom: '4px',
                      borderRadius: '4px',
                      backgroundColor: evaluation.result ? '#D1FAE5' : '#FEE2E2',
                      color: evaluation.result ? '#065F46' : '#991B1B'
                    }}>
                      {evaluation.operator !== 'IF' && <strong>{evaluation.operator}</strong>} {evaluation.condition}: {evaluation.result ? 'âœ“ True' : 'âœ— False'}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Display Messages */}
              <div style={{
                padding: '16px',
                borderRadius: '6px',
                backgroundColor: 'white',
                border: '1px solid #E5E7EB'
              }}>
                <h6 style={{ 
                  fontSize: '13px', 
                  fontWeight: '700', 
                  color: '#374151',
                  margin: '0 0 12px 0' 
                }}>
                  ðŸ“¢ Messages that will be displayed:
                </h6>
                
                {businessRuleTestResult.displayMessage && (
                  <div style={{ 
                    padding: '8px 12px',
                    borderRadius: '4px',
                    backgroundColor: businessRuleTestResult.result ? '#D1FAE5' : '#FEE2E2',
                    color: businessRuleTestResult.result ? '#065F46' : '#991B1B',
                    fontSize: '12px',
                    marginBottom: '8px',
                    fontWeight: '600'
                  }}>
                    {businessRuleTestResult.result ? 'âœ…' : 'âŒ'} {businessRuleTestResult.displayMessage}
                  </div>
                )}
                
                {businessRuleTestResult.warningMessage && (
                  <div style={{ 
                    padding: '8px 12px',
                    borderRadius: '4px',
                    backgroundColor: '#FEF3C7',
                    color: '#92400E',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    âš ï¸ {businessRuleTestResult.warningMessage}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          paddingTop: '24px', 
          borderTop: '1px solid #E5E7EB'
        }}>
          <button
            onClick={handleSaveRule}
            style={{
              flex: 1,
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <span style={{ fontSize: '18px' }}>ðŸ’¾</span>
            {editingRule ? 'Update Rule' : 'Save Rule'}
          </button>
          <button
            onClick={() => {
              setView('library');
              setEditingRule(null);
              setBusinessRuleTestResult(null);
              setTestData({});
            }}
            style={{
              padding: '12px 24px',
              border: '2px solid #D1D5DB',
              borderRadius: '8px',
              backgroundColor: 'white',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              color: '#6B7280'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  // Show "No App Selected" message if appName is not provided
  if (!appName) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
        {/* Custom Navigation Bar */}
        <div style={{
          padding: '12px 24px',
          background: '#050618',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          minHeight: '5.25rem',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          <div 
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              marginRight: '32px'
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src={logo} 
              alt="MUDUMBAI Logo" 
              style={{
                height: '3.5rem',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginLeft: 'auto'
          }}>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '8px 14px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <span>🏠</span>
              <span>Go to Dashboard</span>
            </button>
          </div>
        </div>
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto',
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '40px',
            border: '2px dashed #E5E7EB',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
            }}>
              No App Selected
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              marginBottom: '20px',
            }}>
              Please go back to the dashboard and select an app to manage its business rules and validations.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: '#3B82F6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563EB';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3B82F6';
              }}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      {/* Custom Navigation Bar */}
      <div style={{
        padding: '12px 24px',
        background: '#050618',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        minHeight: '5.25rem',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        {/* Left Section: MUDUMBAI Logo */}
        <div 
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            marginRight: '32px'
          }}
          onClick={() => navigate('/')}
        >
          <img 
            src={logo} 
            alt="MUDUMBAI Logo" 
            style={{
              height: '3.5rem',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Center Section: Page-specific content */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flex: 1
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              ⚙️
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#FFFFFF',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}>
                Business Rules & Validations
              </h1>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.8)'
              }}>
                {appName ? `Managing rules for: ${appName}` : 'Please select an app from the dashboard'}
              </p>
            </div>
          </div>
        </div>

        {/* Right Section: Create New Rule, Go to Dashboard and Profile */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Create New Rule Button - Only show in library view */}
          {view === 'library' && (
            <button
              onClick={() => setView('builder')}
              style={{
                padding: '8px 14px',
                background: 'rgba(59, 130, 246, 0.8)',
                border: '1px solid rgba(59, 130, 246, 0.5)',
                borderRadius: '8px',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(59, 130, 246, 1)';
                e.target.style.borderColor = 'rgba(59, 130, 246, 0.8)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(59, 130, 246, 0.8)';
                e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              }}
            >
              <span style={{ fontSize: '16px' }}>+</span>
              Create New Rule
            </button>
          )}

          {/* Go to Dashboard Button */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '8px 14px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <span>🏠</span>
            <span>Go to Dashboard</span>
          </button>

          {/* Profile Icon */}
          <div style={{ marginLeft: '12px' }}>
            <ProfileMenu
              trigger={
                <div style={{
                  width: '2.75rem',
                  height: '2.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
                >
                  <ProfileIcon userImage={userImage} />
                </div>
              }
            >
              <ProfileMenuItem onClick={() => navigate('/userprofile')}>
                <FaUserCircle size={22} /> Profile
              </ProfileMenuItem>
              <ProfileMenuItem onClick={() => navigate('/settings')}>
                <IoSettingsOutline size={22} /> Setting
              </ProfileMenuItem>
              <ProfileMenuItem>
                <FiLogOut size={20} /> <Logout />
              </ProfileMenuItem>
            </ProfileMenu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ 
        background: 'linear-gradient(135deg, #F9FAFB 0%, #EFF6FF 100%)' 
      }}>
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
        {view === 'library' && renderRuleLibrary()}
        {view === 'builder' && renderRuleBuilder()}
      </div>
    </div>
  );
};

export default BusinessRulesBuilder;


